import React, { useMemo, useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import {
  CalendarDays,
  Check,
  ChevronDown,
  Globe,
  KeyRound,
  Users,
} from "lucide-react";

interface RoomiesProps {
  propertyData: PropertyToEdit | null;
}

/* ---------- helpers ---------- */

const VISIBLE_LIMIT = 6; // roommates shown before "Show all" (tablet & desktop)
const MOBILE_LIMIT = 3; // roommates shown before "Show all" on phones
const SHOW_COUNT_FROM = 0.35; // show "X of Y booked" only from 35% occupancy

function getUpcomingSemester(): string {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  return month >= 8 ? `March – July ${year + 1}` : `August – December ${year}`;
}

// Builds "Germany" -> "DE" once from the browser's region names.
// The first code found for a name wins, so France stays "FR" (not "FX").
const COUNTRY_CODES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  try {
    const names = new Intl.DisplayNames(["en"], { type: "region" });
    for (let i = 0; i < 26; i++) {
      for (let j = 0; j < 26; j++) {
        const code = String.fromCharCode(65 + i, 65 + j);
        const name = names.of(code);
        if (!name || name === code) continue;
        const key = name.toLowerCase();
        if (!map[key]) map[key] = code;
      }
    }
  } catch {
    /* Intl.DisplayNames not supported: no chips */
  }
  return map;
})();

const getCountryCode = (country?: string) =>
  country ? COUNTRY_CODES[country.trim().toLowerCase()] : undefined;

const AVATAR_TINTS = [
  "bg-[#FDE7E0] text-[#8F3520] dark:bg-[#F56948]/20 dark:text-[#FDB8A6]",
  "bg-[#E3ECFB] text-[#1E4A99] dark:bg-blue-500/20 dark:text-blue-200",
  "bg-[#E2F2EC] text-[#17634F] dark:bg-emerald-500/20 dark:text-emerald-200",
  "bg-[#F1E8FA] text-[#5B2F8C] dark:bg-purple-500/20 dark:text-purple-200",
];

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/* ---------- component ---------- */

const Roomies: React.FC<RoomiesProps> = ({ propertyData }) => {
  const [showAll, setShowAll] = useState(false);
  const roomies = propertyData?.roomies ?? [];
  const count = roomies.length;
  const semester = getUpcomingSemester();

  const nationalities = useMemo(
    () =>
      new Set(
        roomies.map((r) => r.country?.trim().toLowerCase()).filter(Boolean)
      ).size,
    [roomies]
  );

  const pastResidents = propertyData?.past_residents; // sent only when 2 or more
  const occupancy = propertyData?.occupancy;
  const occupancyPct = occupancy
    ? Math.min(100, Math.round((occupancy.booked_rooms / occupancy.total_rooms) * 100))
    : 0;
  // Below 35% the count can discourage, so we show "Now booking" instead.
  const showBookedCount =
    !!occupancy &&
    occupancy.total_rooms > 0 &&
    occupancy.booked_rooms / occupancy.total_rooms >= SHOW_COUNT_FROM;

  const visible = showAll ? roomies : roomies.slice(0, VISIBLE_LIMIT);

  /* ---------- header (shared) ---------- */
  const Header = (
    <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          <span className="h-1.5 w-1.5 rounded-full bg-[#F56948]" />
          The household
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-neutral-900 dark:text-white">
          Meet your roommates
        </h2>
      </div>

      <div className="flex w-full items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 py-2 pl-2 pr-4 dark:border-neutral-700 dark:bg-neutral-800/60 sm:w-auto">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-900 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white">
          <CalendarDays size={16} strokeWidth={1.8} />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
            Upcoming semester
          </span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-white">
            {semester}
          </span>
        </div>

        {showBookedCount && occupancy ? (
          <div className="ml-auto flex shrink-0 flex-col gap-1 border-l border-neutral-200 pl-4 leading-tight dark:border-neutral-700">
            <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
              Rooms booked
            </span>
            <span className="text-sm font-semibold text-neutral-900 dark:text-white">
              {occupancy.booked_rooms} of {occupancy.total_rooms}
            </span>
            <div
              className="h-1 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700"
              role="img"
              aria-label={`${occupancy.booked_rooms} of ${occupancy.total_rooms} rooms booked`}
            >
              <div
                className="h-full rounded-full bg-[#F56948]"
                style={{ width: `${occupancyPct}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="ml-auto flex shrink-0 items-center gap-2 self-stretch border-l border-neutral-200 pl-4 dark:border-neutral-700">
            <span className="h-2 w-2 rounded-full bg-[#1F8A70] ring-[3px] ring-[#D9F0E8] dark:ring-emerald-900/60" />
            <span className="text-sm font-semibold text-[#17634F] dark:text-emerald-300">
              Now booking
            </span>
          </div>
        )}
      </div>
    </div>
  );

  /* ---------- state 2: no upcoming roommates, but past residents ---------- */
  if (count === 0 && pastResidents && pastResidents.count >= 2) {
    const shown = pastResidents.initials.slice(0, 4);
    const extra = pastResidents.count - shown.length;

    return (
      <div className="listingSection__wrap w-full">
        {Header}

        <div className="mt-6 grid grid-cols-1 gap-5 rounded-2xl bg-[#F7F5F2] p-5 dark:bg-neutral-800/60 sm:grid-cols-[minmax(0,1fr)_240px] sm:gap-7 sm:p-7">
          <div className="flex flex-col justify-center gap-4">
            <h3 className="text-lg font-bold tracking-tight text-neutral-900 dark:text-white sm:text-xl">
              A house students love living in
            </h3>
            <div className="flex items-center gap-3">
              <div className="flex" aria-hidden="true">
                {shown.map((ini, i) => (
                  <div
                    key={`${ini}-${i}`}
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#F7F5F2] text-xs font-bold tracking-tight dark:border-neutral-800 ${
                      AVATAR_TINTS[i % AVATAR_TINTS.length]
                    } ${i > 0 ? "-ml-1.5" : ""}`}
                  >
                    {ini}
                  </div>
                ))}
                {extra > 0 && (
                  <div className="-ml-1.5 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#F7F5F2] bg-neutral-200 text-xs font-semibold text-neutral-700 dark:border-neutral-800 dark:bg-neutral-700 dark:text-neutral-200">
                    +{extra}
                  </div>
                )}
              </div>
              <span className="text-[13px] text-neutral-500 dark:text-neutral-400">
                Past residents
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-0.5 rounded-xl border border-neutral-200 bg-white px-5 py-4 dark:border-neutral-700 dark:bg-neutral-900">
            <span className="text-[26px] font-bold tracking-tight text-neutral-900 dark:text-white">
              {pastResidents.count}
            </span>
            <span className="text-[13px] text-neutral-500 dark:text-neutral-400">
              students have stayed here
            </span>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2.5 text-[13px] text-neutral-500 dark:text-neutral-400">
          <Globe size={15} strokeWidth={1.8} className="mt-0.5 shrink-0" />
          <span>Every room is open, so you get first pick.</span>
        </div>
      </div>
    );
  }

  /* ---------- state 1: no bookings at all ---------- */
  if (count === 0) {
    return (
      <div className="listingSection__wrap w-full">
        {Header}
        {/* Option A: first pick of the house */}
        <div className="mt-6 flex items-center gap-5 rounded-2xl border border-[#FBE1D8] bg-[#FFF4F0] p-5 dark:border-[#F56948]/30 dark:bg-[#F56948]/10 sm:gap-7 sm:p-7">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-[#B4442A] shadow-[0_2px_8px_rgba(180,68,42,0.10)] dark:bg-neutral-900 dark:text-[#FDB8A6] sm:h-16 sm:w-16">
            <KeyRound size={28} strokeWidth={1.8} aria-hidden="true" />
          </div>
          <h3 className="text-lg font-bold tracking-tight text-neutral-900 dark:text-white sm:text-xl">
            You get first pick of the house
          </h3>
        </div>

        <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { Icon: Check, title: "Any room you want", text: "No one has picked yet" },
            { Icon: Users, title: "Meet them as they join", text: "New roommates show up here" },
            { Icon: Globe, title: "An international home", text: "Students from around the world" },
          ].map(({ Icon, title, text }) => (
            <li key={title} className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-neutral-200 bg-neutral-50 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white">
                <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
              </div>
              <p className="text-[13px] leading-snug text-neutral-600 dark:text-neutral-300">
                <span className="block font-semibold text-neutral-900 dark:text-white">
                  {title}
                </span>
                {text}
              </p>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  /* ---------- with roommates ---------- */
  return (
    <div className="listingSection__wrap w-full">
      {Header}

      {/* Summary strip */}
      <div className="mt-6 border-y border-neutral-200 py-4 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          <div className="flex" aria-hidden="true">
            {roomies.slice(0, 4).map((r, i) => (
              <div
                key={r.id}
                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-xs font-bold dark:border-neutral-900 ${
                  AVATAR_TINTS[i % AVATAR_TINTS.length]
                } ${i > 0 ? "-ml-2.5" : ""}`}
              >
                {r.name?.charAt(0).toUpperCase()}
              </div>
            ))}
            {count > 4 && (
              <div className="-ml-2.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-neutral-200 text-[11px] font-semibold text-neutral-700 dark:border-neutral-900 dark:bg-neutral-700 dark:text-neutral-200">
                +{count - 4}
              </div>
            )}
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            <span className="font-semibold text-neutral-900 dark:text-white">
              {plural(count, "roommate", "roommates")}
            </span>{" "}
            
          </p>
        </div>
      </div>

      {/* Compact roster: 2 columns on wider screens */}
      <ul className="mt-2 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {visible.map((r, i) => {
          const firstName = r.name?.split(" ")[0] ?? "";
          const g = r.gender?.toLowerCase();
          const genderLabel =
            g === "female" ? "Female" : g === "male" ? "Male" : null;
          const code = getCountryCode(r.country);
          const meta = [genderLabel, r.country].filter(Boolean).join(" · ");

          return (
            <li
              key={r.id}
              className={`${
                !showAll && i >= MOBILE_LIMIT ? "hidden sm:flex" : "flex"
              } items-center gap-3 border-b border-neutral-100 py-3 dark:border-neutral-800`}
            >
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  AVATAR_TINTS[i % AVATAR_TINTS.length]
                }`}
                aria-hidden="true"
              >
                {firstName.charAt(0).toUpperCase()}
              </div>
              <div className="flex min-w-0 flex-grow flex-col leading-tight">
                <span className="truncate text-[15px] font-semibold text-neutral-900 dark:text-white">
                  {firstName}
                </span>
                <span className="truncate text-[13px] text-neutral-500 dark:text-neutral-400">
                  {meta}
                </span>
              </div>
              {code && (
                <span className="shrink-0 rounded-full border border-neutral-200 px-2 py-0.5 text-[11px] font-semibold tracking-[0.06em] text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
                  {code}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {/* Phones: button appears above 3 roommates. Tablet/desktop: only above 6. */}
      {count > MOBILE_LIMIT && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className={`${
            count > VISIBLE_LIMIT ? "inline-flex" : "inline-flex sm:hidden"
          } mt-3 items-center gap-1.5 rounded-lg px-1 py-2 text-sm font-semibold text-neutral-900 hover:text-[#F56948] dark:text-white dark:hover:text-[#F56948]`}
        >
          {showAll ? "Show less" : `Show all ${count} roommates`}
          <ChevronDown
            size={16}
            className={`transition-transform ${showAll ? "rotate-180" : ""}`}
          />
        </button>
      )}

      <div className="mt-4 flex items-start gap-2.5 text-[13px] text-neutral-500 dark:text-neutral-400">
        <Globe size={15} strokeWidth={1.8} className="mt-0.5 shrink-0" />
        <span>
          An international home. Share everyday life with students from around
          the world.
        </span>
      </div>
    </div>
  );
};

export default Roomies;